(() => {
  const CUBIC_INCHES_PER_GALLON = 231;
  const FLUID_OUNCES_PER_GALLON = 128;
  const LITERS_PER_GALLON = 3.785411784;
  const CENTIMETERS_PER_INCH = 2.54;
  const MILLILITERS_PER_CUBIC_INCH = 16.387064;
  const GRAMS_PER_WEIGHT_OUNCE = 28.349523125;

  const RESIN_TYPES = {
    crystal: {
      name: 'Crystal Clear Epoxy Resin',
      depthHelp: 'For layered casting projects. Always follow the product instructions for the maximum depth per layer.',
      useNote: 'Crystal Clear Epoxy Resin is designed for clear casting projects up to 1 inch per layer.',
      allowCustomDepth: true,
      defaultDepth: 0.5,
      depths: [
        { value: 0.25, labelIn: '1/4"', labelCm: '0.64 cm' },
        { value: 0.5, labelIn: '1/2"', labelCm: '1.27 cm' },
        { value: 1, labelIn: '1"', labelCm: '2.54 cm' }
      ]
    },
    deep: {
      name: 'Deep Pour Epoxy Resin',
      depthHelp: 'For thick pours from 2 to 4 inches. Always follow the product instructions for the maximum depth per pour.',
      useNote: 'Deep Pour Epoxy Resin is designed for river tables, deep molds, and 2–4 inch pours.',
      allowCustomDepth: true,
      defaultDepth: 2,
      depths: [
        { value: 2, labelIn: '2"', labelCm: '5.08 cm' },
        { value: 3, labelIn: '3"', labelCm: '7.62 cm' },
        { value: 4, labelIn: '4"', labelCm: '10.16 cm' }
      ]
    },
    coating: {
      name: 'Coating Resin',
      depthHelp: 'For self-leveling surface coats. Choose your edge and seal-coat options below.',
      useNote: 'Coating Resin is designed for glossy 1/8–1/4 inch surface coats on tables, counters, tumblers, and artwork.',
      allowCustomDepth: true,
      defaultDepth: 0.125,
      depths: [
        { value: 0.125, labelIn: '1/8"', labelCm: '0.32 cm' },
        { value: 0.25, labelIn: '1/4"', labelCm: '0.64 cm' }
      ]
    },
    powder: {
      name: 'Resin Casting Powder',
      depthHelp: 'For rectangular or round mold estimates. Choose a preset or enter your mold depth.',
      useNote: 'Resin Casting Powder creates smooth, ceramic-look coasters, trays, candle holders, planters, sculptures, and home decor.',
      allowCustomDepth: true,
      defaultDepth: 0.5,
      depths: [
        { value: 0.25, labelIn: '1/4"', labelCm: '0.64 cm' },
        { value: 0.5, labelIn: '1/2"', labelCm: '1.27 cm' },
        { value: 1, labelIn: '1"', labelCm: '2.54 cm' }
      ]
    }
  };

  class EpoxyCoverageCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.form = this.querySelector('[data-calculator-form]');
      this.result = this.querySelector('[data-result]');
      this.error = this.querySelector('[data-error]');
      this.cartError = this.querySelector('[data-cart-error]');
      this.addButton = this.querySelector('[data-add-kits]');
      this.productLink = this.querySelector('[data-product-link]');
      this.currentRecommendation = null;
      this.config = this.readConfig();

      if (!this.form || !this.result || !this.config) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.calculate(true);
      });

      this.querySelectorAll('[data-resin-control]').forEach((control) => {
        control.addEventListener('change', (event) => this.changeResin(event.target.value));
      });

      this.querySelectorAll('[data-unit-control]').forEach((control) => {
        control.addEventListener('change', (event) => this.changeUnit(event.target.value));
      });

      this.querySelectorAll('[data-shape-control]').forEach((control) => {
        control.addEventListener('change', (event) => this.changeShape(event.target.value));
      });

      this.querySelectorAll('[data-powder-method]').forEach((control) => {
        control.addEventListener('change', () => {
          this.updatePowderMethod();
          this.resetResult();
        });
      });

      this.querySelector('[data-thickness]')?.addEventListener('change', () => {
        this.handleDepthSelection();
        this.resetResult();
      });

      this.querySelector('[data-custom-thickness]')?.addEventListener('input', () => {
        this.updateLayerWarning();
        this.resetResult();
      });

      this.querySelectorAll('[data-dimension-input]').forEach((input) => {
        input.addEventListener('input', () => this.resetResult());
      });

      this.querySelector('[data-powder-weight]')?.addEventListener('input', () => this.resetResult());
      this.querySelector('[data-powder-weight-unit]')?.addEventListener('change', () => this.resetResult());
      this.querySelector('[data-powder-capacity]')?.addEventListener('input', () => this.resetResult());

      this.querySelectorAll('[data-edge], [data-seal-coats]').forEach((control) => {
        control.addEventListener('change', () => this.resetResult());
      });

      this.addButton?.addEventListener('click', () => this.addRecommendedKits());

      const selectedShape = this.querySelector('[data-shape-control]:checked')?.value || 'rectangle';
      this.changeShape(selectedShape, false);
      const selectedResin = this.querySelector('[data-resin-control]:checked')?.value || 'crystal';
      this.applyResin(selectedResin, false);
      this.resetResult();
      this.dataset.initialized = 'true';
    }

    readConfig() {
      const section = this.closest('.epoxy-calculator-section');
      const configElement = section?.querySelector('[data-epoxy-calculator-config]');

      if (!configElement) return null;

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        console.error('Epoxy calculator configuration could not be read.', error);
        return null;
      }
    }

    changeResin(nextResin) {
      if (!RESIN_TYPES[nextResin] || nextResin === this.mode) return;
      this.applyResin(nextResin, true);
    }

    changeShape(nextShape, resetResult = true) {
      const shape = nextShape === 'round' ? 'round' : 'rectangle';
      const isRound = shape === 'round';
      const rectangleFields = this.querySelector('[data-rectangle-fields]');
      const roundFields = this.querySelector('[data-round-fields]');

      this.dataset.currentShape = shape;
      if (rectangleFields) rectangleFields.hidden = isRound;
      if (roundFields) roundFields.hidden = !isRound;
      this.updateDimensionRequirements();

      if (resetResult) {
        this.clearError();
        this.clearCartError();
        this.resetResult();
      }
    }

    updateDimensionRequirements() {
      const isDimensionMode = !this.isPowderAlternateMode();
      const isRound = (this.dataset.currentShape || 'rectangle') === 'round';
      const length = this.querySelector('[data-length]');
      const width = this.querySelector('[data-width]');
      const diameter = this.querySelector('[data-diameter]');
      const thickness = this.querySelector('[data-thickness]');

      if (length) length.required = isDimensionMode && !isRound;
      if (width) width.required = isDimensionMode && !isRound;
      if (diameter) diameter.required = isDimensionMode && isRound;
      if (thickness) thickness.required = isDimensionMode;
    }

    applyResin(nextResin, resetResult) {
      const resin = RESIN_TYPES[nextResin] || RESIN_TYPES.crystal;
      this.mode = RESIN_TYPES[nextResin] ? nextResin : 'crystal';
      this.dataset.selectedResin = this.mode;

      const depthHelp = this.querySelector('[data-depth-help]');
      const resinUse = this.querySelector('[data-resin-use]');
      const resultResin = this.querySelector('[data-result-resin]');
      const coatingOptions = this.querySelector('[data-coating-options]');
      const embeddingNotice = this.querySelector('[data-embedding-notice]');
      const powderOptions = this.querySelector('[data-powder-options]');
      const powderRatioNotice = this.querySelector('[data-powder-ratio-notice]');
      const powderRatioCopy = this.querySelector('[data-powder-ratio-copy]');
      const calculateButton = this.querySelector('[data-calculate-button]');
      const resultEmptyCopy = this.querySelector('[data-result-empty-copy]');
      const powderWaterRatio = EpoxyCoverageCalculator.positiveNumber(this.config.powderWaterRatio, 3);

      if (depthHelp) depthHelp.textContent = resin.depthHelp;
      if (resinUse) resinUse.textContent = resin.useNote;
      if (resultResin) resultResin.textContent = resin.name;
      if (coatingOptions) coatingOptions.hidden = this.mode !== 'coating';
      if (powderOptions) powderOptions.hidden = this.mode !== 'powder';
      if (powderRatioNotice) powderRatioNotice.hidden = this.mode !== 'powder';
      if (powderRatioCopy) powderRatioCopy.textContent = `Use ${EpoxyCoverageCalculator.formatRatio(powderWaterRatio)} parts Casting Powder to 1 part water. Add the powder to room-temperature water and follow the product instructions.`;
      if (calculateButton) calculateButton.textContent = this.mode === 'powder' ? 'Calculate My Casting Powder' : 'Calculate My Resin';
      if (resultEmptyCopy) resultEmptyCopy.textContent = this.mode === 'powder'
        ? 'Enter your mold measurements, mold capacity, or dry powder weight, then select Calculate My Casting Powder.'
        : 'Enter your project measurements, then select Calculate My Resin.';
      if (embeddingNotice) embeddingNotice.hidden = this.mode === 'coating' || this.mode === 'powder';

      this.setDepthOptions(resin);
      this.updatePowderMethod();
      this.renderSelectedProduct(resin);

      if (resetResult) {
        this.clearError();
        this.clearCartError();
        this.resetResult();
      }
    }

    setDepthOptions(resin) {
      const select = this.querySelector('[data-thickness]');
      if (!select) return;

      const unit = this.dataset.currentUnit || 'in';
      const options = resin.depths.map((depth) => {
        const option = document.createElement('option');
        option.value = depth.value.toString();
        option.dataset.labelIn = depth.labelIn;
        option.dataset.labelCm = depth.labelCm;
        option.textContent = unit === 'cm' ? depth.labelCm : depth.labelIn;
        option.selected = depth.value === resin.defaultDepth;
        return option;
      });

      if (resin.allowCustomDepth) {
        const customOption = document.createElement('option');
        customOption.value = 'custom';
        customOption.dataset.labelIn = 'Custom depth';
        customOption.dataset.labelCm = 'Custom depth';
        customOption.textContent = 'Custom depth';
        options.push(customOption);
      }

      select.replaceChildren(...options);
      this.handleDepthSelection();
    }

    handleDepthSelection() {
      const isCustom = RESIN_TYPES[this.mode]?.allowCustomDepth === true
        && this.querySelector('[data-thickness]')?.value === 'custom'
        && !this.isPowderAlternateMode();
      const customField = this.querySelector('[data-custom-depth-field]');
      const customInput = this.querySelector('[data-custom-thickness]');

      if (customField) customField.hidden = !isCustom;
      if (customInput) customInput.required = isCustom;
      this.updateLayerWarning();
    }

    isPowderWeightMode() {
      return this.mode === 'powder'
        && this.querySelector('[data-powder-method]:checked')?.value === 'weight';
    }

    isPowderCapacityMode() {
      return this.mode === 'powder'
        && this.querySelector('[data-powder-method]:checked')?.value === 'capacity';
    }

    isPowderAlternateMode() {
      return this.isPowderWeightMode() || this.isPowderCapacityMode();
    }

    updatePowderMethod() {
      const isWeightMode = this.isPowderWeightMode();
      const isCapacityMode = this.isPowderCapacityMode();
      const isDimensionMode = !isWeightMode && !isCapacityMode;
      const customDepth = this.querySelector('[data-custom-thickness]');
      const powderWeight = this.querySelector('[data-powder-weight]');
      const powderCapacity = this.querySelector('[data-powder-capacity]');
      const embeddingNotice = this.querySelector('[data-embedding-notice]');

      this.querySelectorAll('[data-dimension-only]').forEach((element) => {
        element.hidden = !isDimensionMode;
      });

      const powderWeightField = this.querySelector('[data-powder-weight-field]');
      const powderCapacityField = this.querySelector('[data-powder-capacity-field]');
      if (powderWeightField) powderWeightField.hidden = !isWeightMode;
      if (powderCapacityField) powderCapacityField.hidden = !isCapacityMode;
      if (powderWeight) powderWeight.required = isWeightMode;
      if (powderCapacity) powderCapacity.required = isCapacityMode;
      this.updateDimensionRequirements();
      if (customDepth) customDepth.required = false;
      if (embeddingNotice) embeddingNotice.hidden = this.mode === 'coating' || this.mode === 'powder';

      if (isDimensionMode) this.handleDepthSelection();
    }

    updateLayerWarning() {
      const warning = this.querySelector('[data-layer-warning]');
      if (!warning) return;

      const isCustom = this.mode === 'crystal' && this.querySelector('[data-thickness]')?.value === 'custom';
      const customValue = Number(this.querySelector('[data-custom-thickness]')?.value);
      const unit = this.dataset.currentUnit || 'in';
      const depthInches = EpoxyCoverageCalculator.toInches(customValue, unit);
      warning.hidden = !(isCustom && Number.isFinite(depthInches) && depthInches > 1);
    }

    renderSelectedProduct(resin) {
      const preview = this.querySelector('[data-selected-product]');
      if (!preview) return;

      const kits = Array.isArray(this.config.kits)
        ? this.config.kits.filter((kit) => kit.mode === this.mode)
        : [];
      const product = kits.find((kit) => kit.productTitle && kit.productUrl) || kits[0];

      if (!product) {
        preview.hidden = true;
        return;
      }

      const image = preview.querySelector('[data-selected-product-image]');
      const title = preview.querySelector('[data-selected-product-title]');
      const copy = preview.querySelector('[data-selected-product-copy]');
      const sizes = preview.querySelector('[data-selected-product-sizes]');
      const link = preview.querySelector('[data-selected-product-link]');
      const availableSizes = [...new Set(
        kits
          .filter((kit) => kit.available === true)
          .map((kit) => kit.label || kit.variantTitle)
          .filter(Boolean)
      )];

      if (title) title.textContent = product.productTitle || resin.name;
      if (copy) copy.textContent = resin.useNote;
      if (sizes) {
        sizes.textContent = availableSizes.length > 0
          ? `Available sizes: ${availableSizes.join(', ')}`
          : 'See the product page for current sizes and availability.';
      }
      if (link) link.href = product.productUrl || product.url || this.config.allProductsUrl || '/collections/all';

      if (image) {
        const imageUrl = kits.find((kit) => kit.productImage)?.productImage;
        if (imageUrl) {
          image.src = imageUrl;
          image.alt = `${product.productTitle || resin.name} product kit`;
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
        }
      }

      preview.hidden = false;
    }

    changeUnit(nextUnit) {
      const currentUnit = this.dataset.currentUnit || 'in';
      if (!['in', 'cm'].includes(nextUnit) || nextUnit === currentUnit) return;

      this.querySelectorAll('[data-dimension-input]').forEach((input) => {
        const value = Number(input.value);

        if (Number.isFinite(value) && value > 0) {
          const converted = nextUnit === 'cm'
            ? value * CENTIMETERS_PER_INCH
            : value / CENTIMETERS_PER_INCH;
          input.value = EpoxyCoverageCalculator.formatInput(converted);
        }

        input.max = nextUnit === 'cm' ? '25400' : '10000';
      });

      this.querySelectorAll('[data-unit-label]').forEach((label) => {
        label.textContent = nextUnit;
      });

      const customDepth = this.querySelector('[data-custom-thickness]');
      if (customDepth) {
        const value = Number(customDepth.value);
        if (Number.isFinite(value) && value > 0) {
          const converted = nextUnit === 'cm'
            ? value * CENTIMETERS_PER_INCH
            : value / CENTIMETERS_PER_INCH;
          customDepth.value = EpoxyCoverageCalculator.formatInput(converted);
        }
        customDepth.max = nextUnit === 'cm' ? '254' : '100';
      }

      this.querySelectorAll('[data-custom-unit-label]').forEach((label) => {
        label.textContent = nextUnit;
      });

      this.querySelectorAll('[data-thickness] option').forEach((option) => {
        option.textContent = nextUnit === 'cm' ? option.dataset.labelCm : option.dataset.labelIn;
      });

      this.dataset.currentUnit = nextUnit;
      this.updateLayerWarning();

      if (this.result.dataset.state === 'ready') this.calculate(false);
    }

    calculate(shouldFocus) {
      this.clearError();
      this.clearCartError();

      if (this.isPowderWeightMode()) {
        this.calculatePowderByWeight(shouldFocus);
        return;
      }

      if (this.isPowderCapacityMode()) {
        this.calculatePowderByCapacity(shouldFocus);
        return;
      }

      const shape = this.dataset.currentShape || 'rectangle';
      const isRound = shape === 'round';
      const lengthField = this.querySelector('[data-length]');
      const widthField = this.querySelector('[data-width]');
      const diameterField = this.querySelector('[data-diameter]');
      const length = Number(lengthField?.value);
      const width = Number(widthField?.value);
      const diameter = Number(diameterField?.value);
      const unit = this.dataset.currentUnit || 'in';
      const thicknessSelect = this.querySelector('[data-thickness]');
      const customThickness = this.querySelector('[data-custom-thickness]');
      const isCustomDepth = RESIN_TYPES[this.mode]?.allowCustomDepth === true && thicknessSelect?.value === 'custom';
      const enteredThickness = Number(isCustomDepth ? customThickness?.value : thicknessSelect?.value);
      const thicknessInches = isCustomDepth
        ? EpoxyCoverageCalculator.toInches(enteredThickness, unit)
        : enteredThickness;

      const measurementFields = [
        ...(isRound ? [diameterField] : [lengthField, widthField]),
        isCustomDepth ? customThickness : thicknessSelect
      ].filter(Boolean);
      const invalidMeasurementFields = measurementFields.filter((field) => {
        const value = Number(field.value);
        return !Number.isFinite(value) || value <= 0;
      });

      if (invalidMeasurementFields.length > 0) {
        this.showError('Enter a positive number for every measurement.', invalidMeasurementFields);
        return;
      }

      const maxDimension = unit === 'cm' ? 25400 : 10000;
      const maxCustomDepth = unit === 'cm' ? 254 : 100;
      const hasOversizedPlanDimension = isRound
        ? diameter > maxDimension
        : length > maxDimension || width > maxDimension;
      if (hasOversizedPlanDimension || (isCustomDepth && enteredThickness > maxCustomDepth)) {
        const oversizedFields = [
          ...(isRound ? [diameterField] : [lengthField, widthField]),
          isCustomDepth ? customThickness : null
        ].filter((field) => {
          if (!field) return false;
          const limit = field === customThickness ? maxCustomDepth : maxDimension;
          return Number(field.value) > limit;
        });
        this.showError(
          'One or more measurements are too large. Please check your project dimensions.',
          oversizedFields
        );
        return;
      }

      const lengthInches = EpoxyCoverageCalculator.toInches(length, unit);
      const widthInches = EpoxyCoverageCalculator.toInches(width, unit);
      const diameterInches = EpoxyCoverageCalculator.toInches(diameter, unit);

      if (this.mode === 'powder') {
        const calculation = EpoxyCoverageCalculator.calculatePowderFromVolume({
          lengthInches,
          widthInches,
          diameterInches,
          thicknessInches,
          shape,
          powderGramsPerMl: EpoxyCoverageCalculator.positiveNumber(this.config.powderGramsPerMl, 1.4),
          powderWaterRatio: EpoxyCoverageCalculator.positiveNumber(this.config.powderWaterRatio, 3),
          safetyBufferPercent: Number(this.config.safetyBufferPercent || 0)
        });

        if (!Number.isFinite(calculation.powderGrams) || calculation.powderGrams <= 0) {
          this.showError('We could not calculate this mold. Please check your measurements.');
          return;
        }

        if (calculation.powderGrams > Number(this.config.maxPowderGrams || 300000)) {
          this.showError('This project is larger than the online Casting Powder calculator supports. Please contact us for a personalized estimate.');
          return;
        }

        const kits = Array.isArray(this.config.kits)
          ? this.config.kits.filter((kit) => kit.mode === this.mode)
          : [];
        const recommendation = EpoxyCoverageCalculator.recommendKits(calculation.powderGrams, kits, 'capacityGrams');

        this.currentRecommendation = recommendation;
        this.renderPowderResult(calculation, recommendation, kits);
        if (shouldFocus) this.result.focus();
        return;
      }

      const calculation = EpoxyCoverageCalculator.calculateGallons({
        mode: this.mode,
        lengthInches,
        widthInches,
        diameterInches,
        thicknessInches,
        shape,
        edgeOverflow: this.mode === 'coating' && this.querySelector('[data-edge]:checked')?.value === 'overflow',
        sealCoats: this.mode === 'coating' ? Number(this.querySelector('[data-seal-coats]')?.value || 0) : 0,
        edgeAllowancePercent: Number(this.config.edgeAllowancePercent || 0),
        sealCoverageSqFtPerGallon: Number(this.config.sealCoverageSqFtPerGallon || 48),
        safetyBufferPercent: Number(this.config.safetyBufferPercent || 0)
      });

      if (!Number.isFinite(calculation.gallons) || calculation.gallons <= 0) {
        this.showError('We could not calculate this project. Please check your measurements.');
        return;
      }

      if (calculation.gallons > Number(this.config.maxGallons || 250)) {
        this.showError('This project is larger than the online calculator supports. Please contact us for a personalized estimate.');
        return;
      }

      const requiredOz = calculation.gallons * FLUID_OUNCES_PER_GALLON;
      const kits = Array.isArray(this.config.kits)
        ? this.config.kits.filter((kit) => kit.mode === this.mode)
        : [];
      const recommendation = EpoxyCoverageCalculator.recommendKits(requiredOz, kits);

      this.currentRecommendation = recommendation;
      this.renderResult(calculation, recommendation, kits);

      if (shouldFocus) this.result.focus();
    }

    calculatePowderByWeight(shouldFocus) {
      const powderWeight = this.querySelector('[data-powder-weight]');
      const enteredWeight = Number(powderWeight?.value);
      const weightUnit = this.querySelector('[data-powder-weight-unit]')?.value || 'g';
      const powderWaterRatio = EpoxyCoverageCalculator.positiveNumber(this.config.powderWaterRatio, 3);
      const powderGrams = weightUnit === 'oz' ? enteredWeight * GRAMS_PER_WEIGHT_OUNCE : enteredWeight;

      if (!Number.isFinite(enteredWeight) || enteredWeight <= 0 || !Number.isFinite(powderGrams) || powderGrams < 1) {
        this.showError('Enter at least 1 g (0.04 oz) of dry powder.', powderWeight ? [powderWeight] : []);
        return;
      }

      if (!Number.isFinite(powderGrams) || powderGrams > Number(this.config.maxPowderGrams || 300000)) {
        this.showError('This batch is larger than the online Casting Powder calculator supports. Please contact us for a personalized estimate.', powderWeight ? [powderWeight] : []);
        return;
      }

      const calculation = {
        powderGrams,
        waterGrams: powderGrams / powderWaterRatio,
        powderWaterRatio,
        safetyBufferPercent: 0,
        estimateMethod: 'weight'
      };
      const kits = Array.isArray(this.config.kits)
        ? this.config.kits.filter((kit) => kit.mode === this.mode)
        : [];
      const recommendation = EpoxyCoverageCalculator.recommendKits(powderGrams, kits, 'capacityGrams');

      this.currentRecommendation = recommendation;
      this.renderPowderResult(calculation, recommendation, kits);
      if (shouldFocus) this.result.focus();
    }

    calculatePowderByCapacity(shouldFocus) {
      const capacityField = this.querySelector('[data-powder-capacity]');
      const moldCapacityOz = Number(capacityField?.value);
      const powderGramsPerMoldOz = EpoxyCoverageCalculator.positiveNumber(this.config.powderGramsPerMoldOz, 39.3);
      const powderWaterRatio = EpoxyCoverageCalculator.positiveNumber(this.config.powderWaterRatio, 3);

      if (!Number.isFinite(moldCapacityOz) || moldCapacityOz <= 0) {
        this.showError('Enter a mold capacity greater than 0 oz.', capacityField ? [capacityField] : []);
        return;
      }

      const calculation = EpoxyCoverageCalculator.calculatePowderFromMoldCapacity({
        moldCapacityOz,
        powderGramsPerMoldOz,
        powderWaterRatio
      });

      if (!Number.isFinite(calculation.powderGrams) || calculation.powderGrams > Number(this.config.maxPowderGrams || 300000)) {
        this.showError('This mold capacity is larger than the online Casting Powder calculator supports. Please contact us for a personalized estimate.', capacityField ? [capacityField] : []);
        return;
      }

      const kits = Array.isArray(this.config.kits)
        ? this.config.kits.filter((kit) => kit.mode === this.mode)
        : [];
      const recommendation = EpoxyCoverageCalculator.recommendKits(calculation.powderGrams, kits, 'capacityGrams');

      this.currentRecommendation = recommendation;
      this.renderPowderResult(calculation, recommendation, kits);
      if (shouldFocus) this.result.focus();
    }

    renderResult(calculation, recommendation, configuredKits) {
      const requiredOz = calculation.gallons * FLUID_OUNCES_PER_GALLON;
      const liters = calculation.gallons * LITERS_PER_GALLON;
      const volumeHeading = this.querySelector('[data-result-volume]');
      const resultNote = this.querySelector('[data-result-note]');
      const resultResin = this.querySelector('[data-result-resin]');
      const list = this.querySelector('[data-recommendation-list]');
      const coverage = this.querySelector('[data-recommendation-coverage]');
      const recommendationHeading = this.querySelector('[data-recommendation-heading]');
      const disclaimer = this.querySelector('[data-estimate-disclaimer]');
      const resinName = RESIN_TYPES[this.mode]?.name || 'epoxy resin';
      const emptyState = this.querySelector('[data-result-empty]');
      const resultContent = this.querySelector('[data-result-content]');

      if (emptyState) emptyState.hidden = true;
      if (resultContent) resultContent.hidden = false;
      this.result.dataset.state = 'ready';

      if (resultResin) resultResin.textContent = resinName;
      if (recommendationHeading) recommendationHeading.textContent = 'Recommended kits';
      if (disclaimer) disclaimer.textContent = 'Estimate only. Check your measurements and product instructions before starting your pour.';
      if (this.addButton) this.addButton.textContent = 'Add Recommended Kits';
      volumeHeading.textContent = `${EpoxyCoverageCalculator.formatNumber(requiredOz, 1)} oz`;
      resultNote.textContent = `About ${EpoxyCoverageCalculator.formatNumber(calculation.gallons, 2)} gal (${EpoxyCoverageCalculator.formatNumber(liters, 2)} L), including a ${EpoxyCoverageCalculator.formatNumber(calculation.safetyBufferPercent, 0)}% safety allowance.`;
      list.replaceChildren();

      if (recommendation) {
        recommendation.items.forEach((item) => {
          const listItem = document.createElement('li');
          const amount = document.createElement('strong');
          const product = document.createElement('span');

          amount.textContent = `${item.quantity} × ${item.kit.label || item.kit.variantTitle || 'Resin kit'}`;
          product.textContent = item.kit.productTitle || `Let's Resin ${resinName}`;
          listItem.append(amount, product);
          list.append(listItem);
        });

        const recommendedGallons = recommendation.totalCapacity / FLUID_OUNCES_PER_GALLON;
        const coverageParts = [
          `Recommended total: ${EpoxyCoverageCalculator.formatNumber(recommendation.totalCapacity, 1)} oz (${EpoxyCoverageCalculator.formatNumber(recommendedGallons, 2)} gal)`
        ];

        if (recommendation.totalPrice > 0) {
          coverageParts.push(EpoxyCoverageCalculator.formatMoney(recommendation.totalPrice, this.config.currency));
        }

        coverage.textContent = coverageParts.join(' · ');
        this.addButton.disabled = false;
        this.addButton.hidden = false;
        this.productLink.href = recommendation.items[0]?.kit?.url || this.getFallbackUrl(configuredKits);
        this.productLink.textContent = 'View Product';
        this.productLink.hidden = false;
      } else {
        const listItem = document.createElement('li');
        listItem.className = 'epoxy-calculator__unavailable';
        listItem.textContent = 'Kit recommendations are temporarily unavailable, but your resin estimate is ready.';
        list.append(listItem);
        coverage.textContent = 'View the product page to check current sizes and availability.';
        this.addButton.disabled = true;
        this.addButton.hidden = true;
        this.productLink.href = this.getFallbackUrl(configuredKits);
        this.productLink.textContent = 'View Product';
        this.productLink.hidden = false;
      }

      this.result.hidden = false;
    }

    renderPowderResult(calculation, recommendation, configuredKits) {
      const powderOunces = calculation.powderGrams / GRAMS_PER_WEIGHT_OUNCE;
      const waterOunces = calculation.waterGrams / GRAMS_PER_WEIGHT_OUNCE;
      const volumeHeading = this.querySelector('[data-result-volume]');
      const resultNote = this.querySelector('[data-result-note]');
      const resultMaterial = this.querySelector('[data-result-resin]');
      const list = this.querySelector('[data-recommendation-list]');
      const coverage = this.querySelector('[data-recommendation-coverage]');
      const recommendationHeading = this.querySelector('[data-recommendation-heading]');
      const disclaimer = this.querySelector('[data-estimate-disclaimer]');
      const emptyState = this.querySelector('[data-result-empty]');
      const resultContent = this.querySelector('[data-result-content]');

      if (emptyState) emptyState.hidden = true;
      if (resultContent) resultContent.hidden = false;
      this.result.dataset.state = 'ready';

      if (resultMaterial) resultMaterial.textContent = 'Resin Casting Powder';
      if (recommendationHeading) recommendationHeading.textContent = 'Recommended bags';
      if (this.addButton) this.addButton.textContent = 'Add Recommended Bags';
      const gramPrecision = calculation.estimateMethod === 'capacity' ? 1 : null;
      const powderGrams = gramPrecision === null
        ? EpoxyCoverageCalculator.formatWeightGrams(calculation.powderGrams)
        : EpoxyCoverageCalculator.formatNumber(calculation.powderGrams, gramPrecision);
      const waterGrams = gramPrecision === null
        ? EpoxyCoverageCalculator.formatWeightGrams(calculation.waterGrams)
        : EpoxyCoverageCalculator.formatNumber(calculation.waterGrams, gramPrecision);
      volumeHeading.textContent = `${powderGrams} g powder + ${waterGrams} g water`;

      if (calculation.estimateMethod === 'capacity') {
        resultNote.textContent = `For a mold labeled ${EpoxyCoverageCalculator.formatNumber(calculation.moldCapacityOz, 1)} oz, this estimate uses ${EpoxyCoverageCalculator.formatNumber(calculation.powderGramsPerMoldOz, 1)} g of dry powder per 1 oz of mixed mold capacity. Water is calculated at a ${EpoxyCoverageCalculator.formatRatio(calculation.powderWaterRatio)}:1 ratio by weight. No additional safety allowance was added.`;
        if (disclaimer) disclaimer.textContent = 'Mold capacity in oz is a liquid-volume label, not powder weight. Mold markings and actual material yield can vary, so use this result as a planning estimate.';
      } else if (calculation.estimateMethod === 'dimensions') {
        resultNote.textContent = `About ${EpoxyCoverageCalculator.formatNumber(powderOunces, 1)} oz powder + ${EpoxyCoverageCalculator.formatNumber(waterOunces, 1)} oz water, including a ${EpoxyCoverageCalculator.formatNumber(calculation.safetyBufferPercent, 0)}% safety allowance. This mold-volume estimate uses ${EpoxyCoverageCalculator.formatNumber(calculation.powderGramsPerMl, 2)} g of dry powder per finished mL.`;
        if (disclaimer) disclaimer.textContent = 'Estimate only. Mold shape, air pockets, additions, and material yield can change actual use. Run a small measured test and follow the product instructions.';
      } else {
        resultNote.textContent = `Use ${EpoxyCoverageCalculator.formatNumber(powderOunces, 1)} oz of powder with ${EpoxyCoverageCalculator.formatNumber(waterOunces, 1)} oz of water at a ${EpoxyCoverageCalculator.formatRatio(calculation.powderWaterRatio)}:1 ratio by weight. No safety allowance was added.`;
        if (disclaimer) disclaimer.textContent = 'The 3:1 calculation is by weight, not volume. Follow the product instructions and use a digital scale.';
      }

      list.replaceChildren();

      if (recommendation) {
        recommendation.items.forEach((item) => {
          const listItem = document.createElement('li');
          const amount = document.createElement('strong');
          const product = document.createElement('span');

          amount.textContent = `${item.quantity} × ${item.kit.label || item.kit.variantTitle || 'Casting Powder bag'}`;
          product.textContent = item.kit.productTitle || "Let's Resin Casting Powder";
          listItem.append(amount, product);
          list.append(listItem);
        });

        const coverageParts = [
          `Recommended dry powder: ${EpoxyCoverageCalculator.formatNumber(recommendation.totalCapacity, 0)} g (${EpoxyCoverageCalculator.formatNumber(recommendation.totalCapacity / GRAMS_PER_WEIGHT_OUNCE, 1)} oz)`
        ];

        if (recommendation.totalPrice > 0) {
          coverageParts.push(EpoxyCoverageCalculator.formatMoney(recommendation.totalPrice, this.config.currency));
        }

        coverage.textContent = coverageParts.join(' · ');
        this.addButton.disabled = false;
        this.addButton.hidden = false;
        this.productLink.href = recommendation.items[0]?.kit?.url || this.getFallbackUrl(configuredKits);
        this.productLink.textContent = 'View Product';
        this.productLink.hidden = false;
      } else {
        const listItem = document.createElement('li');
        listItem.className = 'epoxy-calculator__unavailable';
        listItem.textContent = 'Bag recommendations are temporarily unavailable, but your mixing estimate is ready.';
        list.append(listItem);
        coverage.textContent = 'View the product page to check current availability.';
        this.addButton.disabled = true;
        this.addButton.hidden = true;
        this.productLink.href = this.getFallbackUrl(configuredKits);
        this.productLink.textContent = 'View Product';
        this.productLink.hidden = false;
      }

      this.result.hidden = false;
    }

    resetResult() {
      const emptyState = this.querySelector('[data-result-empty]');
      const resultContent = this.querySelector('[data-result-content]');

      this.result.hidden = false;
      this.result.dataset.state = 'empty';
      if (emptyState) emptyState.hidden = false;
      if (resultContent) resultContent.hidden = true;
      this.currentRecommendation = null;
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.hidden = false;
      }
      if (this.productLink) this.productLink.hidden = true;
    }

    getFallbackUrl(kits) {
      const kitWithUrl = kits.find((kit) => typeof kit.url === 'string' && kit.url.length > 0);
      return kitWithUrl?.url || this.config.allProductsUrl || '/collections/all';
    }

    async addRecommendedKits() {
      if (!this.currentRecommendation || !this.addButton) return;

      this.clearCartError();
      const defaultText = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = 'Adding Kits…';

      const miniCart = document.querySelector('mini-cart');
      const sections = miniCart?.getSectionsToRender?.().map((section) => section.id) || [];
      const items = this.currentRecommendation.items.map((item) => ({
        id: item.kit.id,
        quantity: item.quantity
      }));

      try {
        const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: `${window.location.pathname}${window.location.search}`
          })
        });
        const parsedState = await response.json();

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'The recommended kits could not be added.');
        }

        if (miniCart && parsedState.sections) {
          miniCart.setActiveElement?.(this.addButton);
          miniCart.renderContents(parsedState);
        } else {
          window.location.assign(this.config.cartUrl || '/cart');
          return;
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'epoxy-calculator',
            cartData: parsedState
          });
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: parsedState }
        }));

        this.addButton.textContent = 'Added to Cart';
      } catch (error) {
        this.showCartError(error.message || 'The recommended kits could not be added. Please try again.');
        this.addButton.textContent = defaultText;
      } finally {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.disabled = false;

        if (this.addButton.textContent === 'Added to Cart') {
          window.setTimeout(() => {
            this.addButton.textContent = defaultText;
          }, 2400);
        }
      }
    }

    showError(message, invalidFields = []) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.resetResult();

      invalidFields.forEach((field) => {
        field.setAttribute('aria-invalid', 'true');
        const descriptions = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        descriptions.add(this.error.id);
        field.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
      });

      (invalidFields[0] || this.error).focus();
    }

    clearError() {
      this.error.textContent = '';
      this.error.hidden = true;

      this.querySelectorAll('[data-length], [data-width], [data-diameter], [data-thickness], [data-custom-thickness]').forEach((field) => {
        field.removeAttribute('aria-invalid');
        const descriptions = (field.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter((id) => id && id !== this.error.id);

        if (descriptions.length > 0) {
          field.setAttribute('aria-describedby', descriptions.join(' '));
        } else {
          field.removeAttribute('aria-describedby');
        }
      });

      this.querySelectorAll('[data-powder-weight], [data-powder-capacity]').forEach((field) => {
        field.removeAttribute('aria-invalid');
        const descriptions = (field.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter((id) => id && id !== this.error.id);

        if (descriptions.length > 0) {
          field.setAttribute('aria-describedby', descriptions.join(' '));
        } else {
          field.removeAttribute('aria-describedby');
        }
      });
    }

    showCartError(message) {
      this.cartError.textContent = message;
      this.cartError.hidden = false;
    }

    clearCartError() {
      this.cartError.textContent = '';
      this.cartError.hidden = true;
    }

    static calculateGallons({
      mode,
      lengthInches,
      widthInches,
      diameterInches,
      thicknessInches,
      shape = 'rectangle',
      edgeOverflow,
      sealCoats,
      edgeAllowancePercent,
      sealCoverageSqFtPerGallon,
      safetyBufferPercent
    }) {
      const geometry = EpoxyCoverageCalculator.calculateGeometry({
        shape,
        lengthInches,
        widthInches,
        diameterInches,
        thicknessInches
      });
      const baseGallons = geometry.volumeCubicInches / CUBIC_INCHES_PER_GALLON;
      let subtotalGallons = baseGallons;

      if (mode === 'coating') {
        const edgeGallons = edgeOverflow ? baseGallons * (edgeAllowancePercent / 100) : 0;
        const areaSqFt = geometry.areaSquareInches / 144;
        const sealGallons = sealCoats > 0
          ? (areaSqFt / sealCoverageSqFtPerGallon) * sealCoats
          : 0;
        subtotalGallons += edgeGallons + sealGallons;
      }

      return {
        gallons: subtotalGallons * (1 + safetyBufferPercent / 100),
        baseGallons,
        safetyBufferPercent
      };
    }

    static calculatePowderFromVolume({
      lengthInches,
      widthInches,
      diameterInches,
      thicknessInches,
      shape = 'rectangle',
      powderGramsPerMl,
      powderWaterRatio,
      safetyBufferPercent
    }) {
      const geometry = EpoxyCoverageCalculator.calculateGeometry({
        shape,
        lengthInches,
        widthInches,
        diameterInches,
        thicknessInches
      });
      const volumeMl = geometry.volumeCubicInches * MILLILITERS_PER_CUBIC_INCH;
      const basePowderGrams = volumeMl * powderGramsPerMl;
      const powderGrams = basePowderGrams * (1 + safetyBufferPercent / 100);

      return {
        powderGrams,
        waterGrams: powderGrams / powderWaterRatio,
        basePowderGrams,
        volumeMl,
        powderGramsPerMl,
        powderWaterRatio,
        safetyBufferPercent,
        estimateMethod: 'dimensions'
      };
    }

    static calculateGeometry({
      shape = 'rectangle',
      lengthInches,
      widthInches,
      diameterInches,
      thicknessInches
    }) {
      const areaSquareInches = shape === 'round'
        ? Math.PI * Math.pow(diameterInches / 2, 2)
        : lengthInches * widthInches;

      return {
        areaSquareInches,
        volumeCubicInches: areaSquareInches * thicknessInches
      };
    }

    static calculatePowderFromMoldCapacity({
      moldCapacityOz,
      powderGramsPerMoldOz,
      powderWaterRatio
    }) {
      const powderGrams = moldCapacityOz * powderGramsPerMoldOz;

      return {
        powderGrams,
        waterGrams: powderGrams / powderWaterRatio,
        moldCapacityOz,
        powderGramsPerMoldOz,
        powderWaterRatio,
        safetyBufferPercent: 0,
        estimateMethod: 'capacity'
      };
    }

    static recommendKits(requiredAmount, configuredKits, capacityKey = 'capacityOz') {
      const kits = configuredKits
        .filter((kit) => (
          kit.available === true
          && Number.isInteger(Number(kit.id))
          && Number(kit.id) > 0
          && Number.isFinite(Number(kit[capacityKey]))
          && Number(kit[capacityKey]) > 0
        ))
        .map((kit) => ({
          ...kit,
          id: Number(kit.id),
          recommendationCapacity: Math.round(Number(kit[capacityKey])),
          price: Math.max(0, Number(kit.price) || 0)
        }));

      if (!Number.isFinite(requiredAmount) || requiredAmount <= 0 || kits.length === 0) return null;

      const target = Math.ceil(requiredAmount - Number.EPSILON);
      const largestKit = Math.max(...kits.map((kit) => kit.recommendationCapacity));
      const limit = target + largestKit;
      const states = new Array(limit + 1);
      states[0] = { price: 0, count: 0, previous: -1, kitIndex: -1 };

      for (let total = 0; total <= limit; total += 1) {
        const state = states[total];
        if (!state) continue;

        kits.forEach((kit, kitIndex) => {
          const next = total + kit.recommendationCapacity;
          if (next > limit) return;

          const candidate = {
            price: state.price + kit.price,
            count: state.count + 1,
            previous: total,
            kitIndex
          };
          const existing = states[next];

          if (
            !existing
            || candidate.price < existing.price
            || (candidate.price === existing.price && candidate.count < existing.count)
          ) {
            states[next] = candidate;
          }
        });
      }

      let selectedTotal = target;
      while (selectedTotal <= limit && !states[selectedTotal]) selectedTotal += 1;
      if (selectedTotal > limit) return null;

      const quantities = new Map();
      let cursor = selectedTotal;

      while (cursor > 0) {
        const state = states[cursor];
        if (!state || state.kitIndex < 0) return null;
        const kit = kits[state.kitIndex];
        const current = quantities.get(kit.id) || { kit, quantity: 0 };
        current.quantity += 1;
        quantities.set(kit.id, current);
        cursor = state.previous;
      }

      const finalState = states[selectedTotal];
      return {
        totalCapacity: selectedTotal,
        totalPrice: finalState.price,
        totalItems: finalState.count,
        items: Array.from(quantities.values()).sort((a, b) => b.kit.recommendationCapacity - a.kit.recommendationCapacity)
      };
    }

    static toInches(value, unit) {
      return unit === 'cm' ? value / CENTIMETERS_PER_INCH : value;
    }

    static formatInput(value) {
      return Number(value.toFixed(2)).toString();
    }

    static formatRatio(value) {
      return Number(Number(value).toFixed(2)).toString();
    }

    static formatWeightGrams(value) {
      return EpoxyCoverageCalculator.formatNumber(value, Math.abs(value) < 10 ? 1 : 0);
    }

    static positiveNumber(value, fallback) {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    static formatNumber(value, maximumFractionDigits) {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits,
        minimumFractionDigits: maximumFractionDigits
      }).format(value);
    }

    static formatMoney(cents, currency) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'USD'
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }
  }

  if (!customElements.get('epoxy-coverage-calculator')) {
    customElements.define('epoxy-coverage-calculator', EpoxyCoverageCalculator);
  }
})();
